import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { MessageSquare, Lock, Video, Star, Pin, TrendingUp, ArrowRight } from "lucide-react";
import api from "@/lib/api";
import { startDiscordConnect } from "@/lib/discordConnect";

const ago = (iso, t) => {
  const mins = Math.max(1, Math.round((Date.now() - new Date(iso).getTime()) / 60000));
  if (mins < 60) return t("discordPulse.minsAgo", { count: mins, defaultValue: "{{count}}m ago" });
  const hrs = Math.round(mins / 60);
  if (hrs < 24) return t("discordPulse.hrsAgo", { count: hrs, defaultValue: "{{count}}h ago" });
  return t("discordPulse.daysAgo", { count: Math.round(hrs / 24), defaultValue: "{{count}}d ago" });
};

function Stat({ icon: Icon, value, label, testId }) {
  return (
    <div data-testid={testId} className="flex items-center gap-3 rounded-xl bg-white border border-slate-200 px-4 py-3">
      <Icon size={16} className="text-[#5865F2] shrink-0" />
      <div>
        <div className="font-display font-black text-2xl text-slate-900 leading-none tabular-nums">{value}</div>
        <div className="mt-1 text-[10px] uppercase tracking-widest font-semibold text-slate-500">{label}</div>
      </div>
    </div>
  );
}

function DeskTeaser({ note, onOpen, t, i }) {
  return (
    <button type="button" onClick={onOpen} data-testid={`discord-pulse-teaser-${i}`}
      className="group text-left rounded-xl border border-slate-200 bg-white p-4 hover:border-[#5865F2]/50 hover:-translate-y-0.5 transition-[transform,border-color] duration-200">
      <div className="flex items-center gap-2">
        <Pin size={12} className="text-amber-600" />
        <span className="font-mono font-bold text-sm text-slate-900">{note.symbol || t("discordPulse.deskNote", { defaultValue: "Desk note" })}</span>
        {note.has_media && (
          <span className="inline-flex items-center gap-1 rounded bg-rose-50 text-rose-700 px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wider">
            <Video size={10} /> {t("discordPulse.video", { defaultValue: "Video" })}
          </span>
        )}
        <span className="ml-auto text-[11px] text-slate-400">{ago(note.captured_at, t)}</span>
      </div>
      <div className="relative mt-3" aria-hidden="true">
        <div className="space-y-1.5 blur-[5px] select-none">
          <div className="h-2.5 rounded bg-slate-300 w-11/12" />
          <div className="h-2.5 rounded bg-slate-300 w-4/5" />
          <div className="h-2.5 rounded bg-slate-300 w-2/3" />
        </div>
        <div className="absolute inset-0 flex items-center justify-center">
          <span className="inline-flex items-center gap-1.5 rounded-full bg-slate-900/85 text-white px-3 py-1 text-[11px] font-semibold">
            <Lock size={11} /> {t("discordPulse.seeInDiscord", { defaultValue: "See it in Discord" })}
          </span>
        </div>
      </div>
    </button>
  );
}

function HallQuote({ h, i }) {
  return (
    <figure data-testid={`discord-pulse-hall-${i}`} className="rounded-xl border border-amber-200 bg-amber-50/60 p-4">
      <Star size={14} className="text-amber-500 fill-amber-400" />
      <blockquote className="mt-2 text-sm text-slate-800 leading-relaxed">"{h.quote}"</blockquote>
      <figcaption className="mt-2 text-xs font-semibold text-slate-600">— {h.author}</figcaption>
    </figure>
  );
}

export default function DiscordPulse({ user }) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [data, setData] = useState(null);

  useEffect(() => {
    if (!user || user.discord_linked) return;
    api.get("/community/discord-pulse").then((r) => setData(r.data)).catch(() => {});
  }, [user]);

  if (!data || data.connected) return null;
  const { counts, desk_teasers: teasers, hall_of_fame: hall } = data;
  if (!counts.outlooks && !counts.desk_notes && !counts.featured) return null;

  const connect = () => startDiscordConnect(navigate);

  return (
    <section data-testid="dashboard-discord-pulse" className="rounded-2xl border border-[#5865F2]/20 bg-gradient-to-b from-[#5865F2]/[0.06] to-transparent p-6 sm:p-8 space-y-6">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <div className="inline-flex items-center gap-1.5 text-[10px] uppercase tracking-[0.28em] font-bold text-[#5865F2]">
            <MessageSquare size={12} /> {t("discordPulse.eyebrow", { defaultValue: "From the Discord · last 7 days" })}
          </div>
          <h2 className="mt-2 font-display font-black text-2xl sm:text-3xl text-slate-900 tracking-tight">
            {t("discordPulse.headline", { defaultValue: "Here's what you missed this week" })}
          </h2>
        </div>
        <button type="button" onClick={connect} data-testid="discord-pulse-connect"
          className="inline-flex items-center gap-2 rounded-full bg-[#5865F2] hover:bg-[#4752c4] text-white px-6 h-11 text-sm font-semibold transition-colors">
          {t("discordPulse.cta", { defaultValue: "Connect Discord" })} <ArrowRight size={14} />
        </button>
      </div>

      <div className="flex flex-col sm:flex-row gap-3 [&>*]:flex-1">
        {counts.outlooks > 0 && <Stat icon={TrendingUp} value={counts.outlooks} label={t("discordPulse.statOutlooks", { defaultValue: "Market Outlooks" })} testId="discord-pulse-stat-outlooks" />}
        {counts.desk_notes > 0 && <Stat icon={Pin} value={counts.desk_notes} label={t("discordPulse.statDesk", { defaultValue: "Desk notes" })} testId="discord-pulse-stat-desk" />}
        {counts.featured > 0 && <Stat icon={Star} value={counts.featured} label={t("discordPulse.statFeatured", { defaultValue: "Members featured" })} testId="discord-pulse-stat-featured" />}
      </div>

      {teasers.length > 0 && (
        <div>
          <div className="text-xs uppercase tracking-widest font-semibold text-slate-500 mb-3">
            {t("discordPulse.deskTitle", { defaultValue: "📌 Desk notes you haven't seen" })}
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
            {teasers.map((n, i) => <DeskTeaser key={i} i={i} note={n} onOpen={connect} t={t} />)}
          </div>
        </div>
      )}

      {hall.length > 0 && (
        <div>
          <div className="text-xs uppercase tracking-widest font-semibold text-slate-500 mb-3">
            {t("discordPulse.hallTitle", { defaultValue: "⭐ Hall of Fame this week" })}
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
            {hall.map((h, i) => <HallQuote key={i} i={i} h={h} />)}
          </div>
          <p className="mt-3 text-sm text-slate-600">
            {t("discordPulse.hallCta", { defaultValue: "Get featured: share your wins in Discord." })}
          </p>
        </div>
      )}
    </section>
  );
}
